import {useExternalAnchor, type PropsWithPartialAnchor} from '@github-ui/react-core/react-partial-anchor'
import type {Repository} from '@github-ui/current-repository'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useEffect, useState, useCallback} from 'react'
import {AnchoredOverlay} from '@primer/react'
import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {GlobalCopilotOverlay} from './GlobalCopilotOverlay'
import {CCAPopover} from './CCAPopover'
import {verifiedFetch} from '@github-ui/verified-fetch'

// Custom event name for toggling the mission control overlay
export const TOGGLE_COPILOT_OVERLAY = 'toggle-copilot-overlay'

export function GlobalAgentsMenu(props: PropsWithPartialAnchor<{repository: Repository; popoverAvailable?: boolean}>) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {repository, popoverAvailable} = props
  const handleClose = () => {
    setOpen(false)
  }

  const [showPopover, setShowPopover] = useState(popoverAvailable || false)
  const dismissCCAPopover = useCallback(async () => {
    setShowPopover(false)
    await verifiedFetch('/settings/dismiss-notice/copilot_coding_agent_entry_point_popover', {method: 'POST'})
  }, [setShowPopover])

  useEffect(() => {
    if (open && showPopover) {
      dismissCCAPopover()
    }
  }, [open, dismissCCAPopover, showPopover])

  useEffect(() => {
    if (open) {
      sendClickAnalyticsEvent({
        category: 'copilot-coding-agent-global-dropdown',
        action: 'open',
      })
    }
  }, [open, sendClickAnalyticsEvent])

  // Listen for the custom keyboard event to open the copilot overlay
  useEffect(() => {
    const handleToggleCopilotOverlay = () => {
      // Until Primer React Tooltip bug is fixed, temporarily hide zombie tooltip when opening overlay to prevent dual tooltips
      // https://github.com/github/primer/issues/5518
      const tooltip = anchorRef.current?.nextElementSibling
      if (!open && tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.setAttribute('style', 'display: none')
      }
      setOpen(!open)
    }

    document.addEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    return () => {
      document.removeEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    }
  }, [setOpen, open, anchorRef])

  // Until Primer React Tooltip bug is fixed, restores the tooltip functionality
  // https://github.com/github/primer/issues/5518
  useEffect(() => {
    if (!open) {
      const tooltip = anchorRef.current?.nextElementSibling
      if (tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.removeAttribute('style')
      }
    }
  }, [open, anchorRef])

  return (
    <>
      <CCAPopover showPopover={showPopover} dismissPopover={dismissCCAPopover} />
      <AnchoredOverlay
        width="large"
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        align="end"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        anchorRef={anchorRef}
        renderAnchor={null}
        preventOverflow={false}
        focusZoneSettings={{disabled: true}}
      >
        <CurrentRepositoryProvider repository={repository}>
          <GlobalCopilotOverlay onClose={handleClose} />
        </CurrentRepositoryProvider>
      </AnchoredOverlay>
    </>
  )
}

try{ GlobalAgentsMenu.displayName ||= 'GlobalAgentsMenu' } catch {}